import { useState } from 'react';
import { RecordingModeSection, type ProjectRecordingMode } from './RecordingModeSection';
import { PersonalRecordingSettingModal } from './PersonalRecordingSettingModal';
import settings from '../../routes/settings/Settings.module.css';
import styles from './WalkthroughRecordingTab.module.css';

/**
 * Walkthrough recording, as its own tab (TOADO-383).
 *
 * Two jobs. The first is the project's setting: what a recording of this
 * project captures. The second is teaching the feature at all, which is why
 * most of this is screenshots of the extension - the recorder and the in-page
 * control live in the browser, not in the web app, so somebody reading this
 * page has no way to see them otherwise.
 */
export function WalkthroughRecordingTab({ mode, onChangeMode }: {
  mode: ProjectRecordingMode;
  onChangeMode: (mode: ProjectRecordingMode) => Promise<void>;
}) {
  const [explainPersonal, setExplainPersonal] = useState(false);

  return (
    <>
      <h2 className={settings.h1}>Walkthrough recording</h2>
      <p className={settings.sub}>
        Talk through a list of problems once, and let Toado file the tickets.
      </p>
      <p className={settings.sub}>
        You start recording, then Alt+click each thing as you mention it. Every click starts
        the next ticket, so the one you are on collects whatever you say until you pin another.
        When you stop, each pin has become a ticket of its own, carrying a screenshot of the
        element you pointed at, the page&rsquo;s console and network, and a clip of just the part
        where you were talking about it.
      </p>
      <p className={settings.sub}>
        It is for the moment when you have eight things to report and no appetite for writing
        eight tickets.
      </p>

      <RecordingModeSection
        mode={mode}
        onChange={onChangeMode}
        heading="What this project records"
        inheritFooter={
          <button type="button" className={styles.learnMore} onClick={() => setExplainPersonal(true)}>
            Where does each person choose theirs?
          </button>
        }
      />

      <section className={settings.section}>
        <h3 className={styles.stepTitle} style={{ marginBottom: 4 }}>Recording a walkthrough</h3>
        <p className={settings.sub} style={{ marginTop: 0 }}>
          Recording starts from the in-page control, not from this page. Here is how to get to
          it and what it does.
        </p>

        <div className={styles.steps}>
          <div className={styles.step}>
            <h4 className={styles.stepTitle}>1. Open Page pins on the site you are reviewing</h4>
            <p className={styles.stepBody}>
              Click the Toado icon in the browser toolbar, then Page pins. On any page whose
              address matches this project&rsquo;s URL patterns the control opens on its own, so
              this is mostly for the first time on a site, for a page you have not set a pattern
              for yet, or to bring the control back after closing it.
            </p>
            <span className={styles.shotFrame}>
              <img
                className={styles.shot}
                src="/help/walkthrough/popup-page-pins-button.png"
                alt="The browser toolbar with the Toado icon, and the extension popup below it with the Page pins button next to Capture."
                width={503}
                height={410}
              />
            </span>
          </div>

          <div className={styles.step}>
            <h4 className={styles.stepTitle}>2. The control sits on the page</h4>
            <p className={styles.stepBody}>
              It starts as a small chip and opens when you click it. The number is how many pins
              this page already has.
            </p>
            <div className={styles.shotRow}>
              <figure className={styles.shotCard}>
                <span className={styles.shotFrame}>
                  <img
                    className={styles.shot}
                    src="/help/walkthrough/control-collapsed.png"
                    alt="A small Page pins chip showing a count of zero."
                    width={217}
                    height={98}
                  />
                </span>
                <figcaption className={styles.shotCaption}>
                  <strong>Closed</strong>
                  Out of the way until you want it. Drag it if it covers something.
                </figcaption>
              </figure>

              <figure className={styles.shotCard}>
                <span className={styles.shotFrame}>
                  <img
                    className={styles.shot}
                    src="/help/walkthrough/control-expanded.png"
                    alt="The chip expanded to show New pin, Show markers and Record buttons."
                    width={289}
                    height={221}
                  />
                </span>
                <figcaption className={styles.shotCaption}>
                  <strong>Open</strong>
                  New pin files one ticket. Show markers puts a marker on every element already
                  pinned. Record starts a walkthrough.
                </figcaption>
              </figure>

              <figure className={styles.shotCard}>
                <span className={styles.shotFrame}>
                  <img
                    className={styles.shot}
                    src="/help/walkthrough/control-panel.png"
                    alt="The full Pins on this page panel, showing the project and column pins are filed into and the list of pins."
                    width={427}
                    height={380}
                  />
                </span>
                <figcaption className={styles.shotCaption}>
                  <strong>The panel</strong>
                  Every pin on the page, and the project and column new ones are filed into.
                  Change either before you start.
                </figcaption>
              </figure>
            </div>
          </div>

          <div className={styles.step}>
            <h4 className={styles.stepTitle}>3. One at a time, or all at once</h4>
            <p className={styles.stepBody}>
              <strong>One at a time.</strong> Press Alt+C, or click New pin, then click the thing
              you are talking about. You get a box to type a title and description, and it files
              one ticket with a screenshot of the page and the element you clicked.
            </p>
            <p className={styles.stepBody}>
              <strong>All at once.</strong> Click Record, talk through the problems, and Alt+click
              each thing as you mention it. Each Alt+click starts a new ticket, so the ticket you
              are on gets whatever you say until you pin the next one. When you stop, every pin
              becomes a ticket with its own screenshot and its own clip of what you said. This is
              the faster way through a long list, and it is what the setting at the top of this
              page applies to.
            </p>
          </div>
        </div>
      </section>

      <PersonalRecordingSettingModal
        open={explainPersonal}
        onClose={() => setExplainPersonal(false)}
      />
    </>
  );
}
